import * as React from 'react';

import { enumUnit, Unit } from '@rematter/frontend-lib-api-client';
import { useWeightConversion } from '@rematter/modules/trade/hooks/useWeightConversion';
import { convertFromPounds } from '@rematter/ui-web';
import { LucideWeight } from 'lucide-react';

import { RequestedQuantityFieldCellProps } from '../types';

import { enumLoadWeightType } from '~/modules/trade/types/load';
import { WeightFieldCell } from '~/shared/components/line-items';

export interface ExtendedRequestedQuantityFieldCellProps extends RequestedQuantityFieldCellProps {
  showWeightIcon?: boolean;
  onChangeRequestedQuantity?: (lineItemId: string, value: number) => void;
}

export const RequestedQuantityFieldCell: React.FC<
  ExtendedRequestedQuantityFieldCellProps
> = ({
  lineItem,
  disabled = false,
  onUpdateLineItem,
  weightUnit = 'price',
  facilityDefaultUnit = enumUnit.Pounds,
  showWeightIcon = false,
  onChangeRequestedQuantity,
}) => {
  const {
    convertToDisplayUnit,
    convertFromDisplayUnit,
    massUnitAbbreviation,
    getDisplayUnit,
  } = useWeightConversion({
    weightUnit,
    facilityDefaultUnit,
    pricingUnit: lineItem.pricingUnit as Unit,
  });

  const displayUnit = React.useMemo(
    () => getDisplayUnit(lineItem.pricingUnit as Unit),
    [getDisplayUnit, lineItem.pricingUnit],
  );

  const minimumValue = React.useMemo(() => {
    if (lineItem.type === 'Tagged' && lineItem.aggregatedTags?.length) {
      return lineItem.aggregatedTags.reduce(
        (sum, tag) => sum + Number(tag.netQuantity ?? 0),
        0,
      );
    }

    return 0;
  }, [lineItem.type, lineItem.aggregatedTags]);

  const handleChange = React.useCallback(
    (value: number) => {
      const minimumInDisplayUnit =
        minimumValue > 0 ? convertFromPounds(minimumValue, displayUnit) : 0;

      const finalValue =
        value < minimumInDisplayUnit ? minimumInDisplayUnit : value;

      const convertedValue = convertFromDisplayUnit(
        finalValue,
        enumUnit.Pounds,
      );

      if (onChangeRequestedQuantity) {
        onChangeRequestedQuantity(lineItem.id, convertedValue);
      } else {
        onUpdateLineItem(lineItem.id, {
          weights: {
            ...lineItem.weights,
            [enumLoadWeightType.Net]: {
              value: convertedValue,
              unit: enumUnit.Pounds,
              __typename: 'Weight' as const,
            },
          },
        });
      }
    },
    [
      lineItem.id,
      lineItem.weights,
      convertFromDisplayUnit,
      onUpdateLineItem,
      onChangeRequestedQuantity,
      minimumValue,
      displayUnit,
    ],
  );

  const displayRequestedQuantity = React.useMemo(
    () => convertToDisplayUnit(lineItem.weights?.[enumLoadWeightType.Net]),
    [lineItem.weights, convertToDisplayUnit],
  );

  return (
    <WeightFieldCell
      value={displayRequestedQuantity}
      onChange={handleChange}
      disabled={disabled}
      prefix={
        showWeightIcon ? (
          <LucideWeight className="size-4" strokeWidth={1.5} />
        ) : undefined
      }
      suffix={massUnitAbbreviation}
      className="@RequestedQuantityFieldCell"
    />
  );
};
